<template>
  <div class="charging-point-configuration-batches">
    <v-card outlined>
      <v-card-subtitle>{{ $t('charging-point-configuration-batches.explained') }}</v-card-subtitle>
      <v-data-table
          :headers="listHeaders"
          :items="list ? list.rows : []"
          :loading="loading"
          :server-items-length="list ? list.total : 0"
          :options.sync="listOptions"
          :footer-props="{'items-per-page-options': [20, 50, 100]}"
          :no-data-text="$t('charging-point-configuration-batches.none')"
          class="clickable-rows"
          @click:row="open"
      >
        <template #item.createdAt="{item}">{{ formatDate(item.createdAt, 'DD/MM/YYYY HH:mm') }}</template>
        <template #item.change="{item}">
          <span class="mono">{{ item.code }} = {{ display(item.value) }}</span>
          <v-chip v-if="item.revertOf" x-small outlined class="ml-1">{{ $t('charging-point-configuration-batches.revert') }}</v-chip>
          <div class="text-caption text--secondary">{{ scope(item) }}</div>
        </template>
        <template #item.progress="{item}">
          <v-progress-linear :value="item.total ? done(item) * 100 / item.total : 0" height="8" rounded :color="item.running ? 'primary' : 'success'" />
          <div class="mt-1">
            <v-chip v-for="status in shownStatuses(item)" :key="status" x-small :color="STATUS_COLORS[status]" text-color="white" class="mr-1">{{ $t('charging-point-configuration-batches.statuses.' + status) }} {{ item.counts[status] }}</v-chip>
          </div>
        </template>
        <template #item.running="{item}">{{ item.running ? $t('charging-point-configuration-batches.running') : $t('charging-point-configuration-batches.finished') }}</template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="detailOpen" max-width="1100" scrollable>
      <v-card v-if="detail">
        <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px">
          <span class="mono">{{ detail.code }} = {{ display(detail.value) }}</span>
          <v-spacer />
          <v-btn small text :disabled="!detail.itemsTotal" :loading="exporting" @click="exportCsv"><v-icon small left>mdi-download</v-icon>CSV</v-btn>
          <v-btn v-if="detail.running" small outlined color="error" :loading="acting" @click="cancel">{{ $t('charging-point-configuration-batches.cancel') }}</v-btn>
          <v-btn v-else-if="detail.counts.accepted || detail.counts['reboot-required']" small outlined :loading="acting" @click="revert">{{ $t('charging-point-configuration-batches.revert-action') }}</v-btn>
          <v-btn icon small :aria-label="$t('close')" @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle>
          {{ $t('charging-point-configuration-batches.by', {name: detail.createdByName || '-', date: formatDate(detail.createdAt, 'DD/MM/YYYY HH:mm')}) }} · {{ scope(detail) }}
          <template v-if="detail.cancelledByName"> · {{ $t('charging-point-configuration-batches.cancelled-by', {name: detail.cancelledByName}) }}</template>
        </v-card-subtitle>
        <v-card-text class="pb-0">
          <!-- Un clic sur un état ne montre que ces bornes -->
          <v-chip-group v-model="statusFilter" column>
            <v-chip v-for="status in shownStatuses(detail)" :key="status" :value="status" small filter outlined>
              {{ $t('charging-point-configuration-batches.statuses.' + status) }} · {{ detail.counts[status] }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
        <v-card-text class="pa-0">
          <v-data-table
              :headers="itemHeaders"
              :items="detail.items"
              :loading="loadingItems"
              :server-items-length="detail.itemsTotal"
              :options.sync="itemOptions"
              :footer-props="{'items-per-page-options': [50, 100, 200]}"
              dense
          >
            <template #item.name="{item}">
              <router-link :to="{name: 'charging-points.detail.manage', params: {id: item.chargingPointId}}">{{ item.name || item.identifier || item.chargingPointId }}</router-link>
              <v-icon x-small :color="item.connected ? 'success' : 'grey'" class="ml-1">mdi-circle</v-icon>
            </template>
            <template #item.key="{item}"><span class="mono">{{ item.key }}</span></template>
            <template #item.before="{item}"><span class="mono">{{ display(item.before) }}</span><span v-if="item.value !== null" class="mono"> → {{ display(item.value) }}</span></template>
            <template #item.status="{item}">
              <v-chip x-small :color="STATUS_COLORS[item.status]" text-color="white">{{ $t('charging-point-configuration-batches.statuses.' + item.status) }}</v-chip>
              <span v-if="item.attempts" class="text-caption text--secondary ml-1">{{ $tc('charging-point-configuration-batches.attempts', item.attempts, {count: item.attempts}) }}</span>
            </template>
            <template #item.current="{item}"><span class="mono">{{ display(item.current) }}</span></template>
            <template #item.answeredAt="{item}">{{ item.answeredAt ? formatDate(item.answeredAt, 'DD/MM HH:mm') : (item.sentAt ? formatDate(item.sentAt, 'DD/MM HH:mm') : '') }}</template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ChargingPointConfigurationRepository from "@repository/ChargingPointConfigurationRepository";
import {downloadCsv} from "@includes/csv";

/** Toutes les 10 secondes tant qu'une modification est en cours. */
const REFRESH_MS = 10000

/**
 * Les modifications en masse : une valeur envoyée petit à petit aux bornes choisies (les connectées d'abord, les autres
 * quand elles se connectent, 7 jours au plus), chacune suivie jusqu'à sa réponse et à la valeur relue. On en ouvre une
 * pour voir ses bornes par état, l'arrêter, ou revenir en arrière une fois finie (chaque borne retrouve sa valeur).
 */
export default {
  props: {
    // Ouvrir ce lot à l'arrivée (lien depuis la création d'un lot).
    openId: {type: String, default: null},
  },

  data() {
    return {
      list: null,
      loading: false,
      listOptions: {page: 1, itemsPerPage: 20},
      detail: null,
      detailOpen: false,
      statusFilter: null,
      itemOptions: {page: 1, itemsPerPage: 50},
      loadingItems: false,
      acting: false,
      exporting: false,
      timer: null,
      STATUS_COLORS: {pending: 'grey', sent: 'primary', accepted: 'success', 'reboot-required': 'teal', rejected: 'error', 'not-supported': 'deep-orange', 'no-answer': 'blue-grey', 'not-connected': 'blue-grey darken-2', failed: 'red darken-3', cancelled: 'grey darken-1'},
    }
  },

  computed: {
    listHeaders() {
      return [
        {text: this.$t('charging-point-configuration-batches.date'), value: 'createdAt', sortable: false, width: 150},
        {text: this.$t('charging-point-configuration-batches.change'), value: 'change', sortable: false},
        {text: this.$t('charging-point-configuration-batches.author'), value: 'createdByName', sortable: false},
        {text: this.$t('charging-point-configuration-batches.progress'), value: 'progress', sortable: false, width: '35%'},
        {text: '', value: 'running', sortable: false},
      ]
    },
    itemHeaders() {
      return [
        {text: this.$t('charging-point-fleet-configuration.charging-point'), value: 'name', sortable: false},
        {text: this.$t('charging-point-configuration-batches.key'), value: 'key', sortable: false},
        {text: this.$t('charging-point-configuration-batches.before'), value: 'before', sortable: false},
        {text: this.$t('charging-point-configuration-batches.status'), value: 'status', sortable: false},
        {text: this.$t('charging-point-configuration-batches.current'), value: 'current', sortable: false},
        {text: this.$t('charging-point-configuration-batches.at'), value: 'answeredAt', sortable: false},
      ]
    },
  },

  watch: {
    listOptions: {
      handler() {
        this.load()
      },
      deep: true,
    },
    itemOptions: {
      handler() {
        this.loadItems()
      },
      deep: true,
    },
    statusFilter() {
      this.itemOptions = {...this.itemOptions, page: 1}
    },
    detailOpen(open) {
      if (!open) {
        this.detail = null
      }
    },
  },

  mounted() {
    if (this.openId) {
      this.open({id: this.openId})
    }
  },

  beforeDestroy() {
    clearTimeout(this.timer)
  },

  methods: {
    load() {
      clearTimeout(this.timer)
      const {page, itemsPerPage} = this.listOptions
      this.loading = true
      return ChargingPointConfigurationRepository.batches({offset: (page - 1) * itemsPerPage, limit: itemsPerPage}).then(list => {
        this.list = list
        if (list.rows.some(batch => batch.running)) {
          this.timer = setTimeout(() => {
            this.load()
            if (this.detailOpen && this.detail && this.detail.running) {
              this.loadItems()
            }
          }, REFRESH_MS)
        }
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    open(batch) {
      this.detail = {items: [], itemsTotal: 0, counts: {}, filters: {}, ...batch}
      this.statusFilter = null
      this.detailOpen = true
      this.itemOptions = {...this.itemOptions, page: 1}
    },

    loadItems() {
      if (!this.detail) {
        return
      }
      const {page, itemsPerPage} = this.itemOptions
      this.loadingItems = true
      ChargingPointConfigurationRepository.batch(this.detail.id, {status: this.statusFilter, offset: (page - 1) * itemsPerPage, limit: itemsPerPage}).then(detail => {
        this.detail = detail
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loadingItems = false
      })
    },

    cancel() {
      this.$dialog.confirm({text: this.$t('charging-point-configuration-batches.cancel-confirm')}).then(result => {
        if (!result) {
          return
        }
        this.acting = true
        ChargingPointConfigurationRepository.cancelBatch(this.detail.id).then(() => {
          this.loadItems()
          return this.load()
        }).catch(err => {
          this.notifyError(err)
        }).finally(() => {
          this.acting = false
        })
      })
    },

    revert() {
      const count = (this.detail.counts.accepted || 0) + (this.detail.counts['reboot-required'] || 0)
      this.$dialog.confirm({text: this.$tc('charging-point-configuration-batches.revert-confirm', count, {count})}).then(result => {
        if (!result) {
          return
        }
        this.acting = true
        ChargingPointConfigurationRepository.revertBatch(this.detail.id).then(({id, chargingPoints}) => {
          this.notifySuccess('charging-point-configuration-batches.reverted', {count: chargingPoints})
          this.load()
          this.open({id})
        }).catch(err => {
          this.notifyError(err)
        }).finally(() => {
          this.acting = false
        })
      })
    },

    // Toutes les bornes du lot (dans l'état choisi), par pages de 200.
    async exportCsv() {
      this.exporting = true
      try {
        const rows = []
        for (let offset = 0; offset < this.detail.itemsTotal; offset += 200) {
          const page = await ChargingPointConfigurationRepository.batch(this.detail.id, {status: this.statusFilter, offset, limit: 200})
          rows.push(...page.items)
        }
        downloadCsv(`modification-${this.detail.code}.csv`, [
          {text: this.$t('charging-point-fleet-configuration.charging-point'), value: row => row.name || row.identifier || row.chargingPointId},
          {text: this.$t('charging-point-fleet-configuration.organization'), value: 'organizationName'},
          {text: this.$t('charging-point-configuration-batches.key'), value: 'key'},
          {text: this.$t('charging-point-configuration-batches.before'), value: 'before'},
          {text: this.$t('charging-point-configuration-batches.status'), value: row => this.$t('charging-point-configuration-batches.statuses.' + row.status)},
          {text: this.$t('charging-point-configuration-batches.current'), value: 'current'},
          {text: this.$t('charging-point-configuration-batches.at'), value: row => row.answeredAt || row.sentAt},
        ], rows)
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.exporting = false
      }
    },

    done(batch) {
      return batch.total - (batch.counts.pending || 0) - (batch.counts.sent || 0)
    },

    shownStatuses(batch) {
      return Object.keys(batch.counts || {}).filter(status => batch.counts[status] > 0)
    },

    // Le périmètre choisi à la création : les filtres, sinon tout le parc.
    scope(batch) {
      const filters = Object.keys(batch.filters || {})
      return filters.length ? this.$t('charging-point-configuration-batches.scope-filtered', {filters: filters.map(filter => this.$t('charging-point-configuration-batches.filters.' + filter)).join(', ')}) : this.$t('charging-point-configuration-batches.scope-fleet')
    },

    display(value) {
      return value === null || value === undefined ? '∅' : (value === '' ? '""' : value)
    },
  },
}
</script>

<style scoped lang="scss">
.mono {
  font-family: monospace;
}
.clickable-rows ::v-deep tbody tr {
  cursor: pointer;
}
</style>
